import { useState } from 'react';
import {
  GridRowModes,
  GridToolbarContainer,
  GridActionsCellItem,
  GridRowEditStopReasons,
} from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import CheckIcon from '@mui/icons-material/Check';

import { dataGridPremiumColumns } from '../../../utils/helper';

import {
  DataGridIdentifiers,
  FormErrorUI,
  AutoCompleteSelectUI,
  CheckboxUI,
  DatePickerUI,
  AddButtonUi,
  DatagridPremiumUi,
} from '../../Interface';

function EditToolbar(props) {
  const { setRowModesModel, disabled, push, defaultResourceOne, params, dynProps } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    let isNotificationYes = false;

    if (params.PROJECT_NOTIFICATION_OBLIGATION_ID) {
      const notificationFieldId = parseInt(params.PROJECT_NOTIFICATION_OBLIGATION_ID);
      const dynamicKey = `din_${notificationFieldId}`;
      const selectedOptionId = values.dynamicFields?.[dynamicKey];

      const dynField = dynProps.find((prop) => prop.propertyId === notificationFieldId);
      const selectedOption = dynField?.property?.propertyOption?.find((opt) => opt.id === selectedOptionId);

      isNotificationYes = selectedOption?.name === 'Igen';
    }

    const newResource = {
      ...defaultResourceOne,
      id: uuidv4(),
      registered: isNotificationYes ? false : true,
    };

    await push(newResource);

    setRowModesModel((prevModel) => ({
      ...prevModel,
      [newResource.id]: { mode: GridRowModes.Edit },
    }));

    const fieldsToValidate = [
      `resourceOne.${values.resourceOne.length ?? 0}.resourceId`,
      `resourceOne.${values.resourceOne.length ?? 0}.leader`,
      `resourceOne.${values.resourceOne.length ?? 0}.educationDate`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
}

const ResourceOneTable = ({ params, disabled, defaultResourceOne, autoCompleteOptions, dynProperties }) => {
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const { dynProps } = dynProperties;

  const [rowModesModel, setRowModesModel] = useState({});

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const getValueIndex = (values, id) => {
    return values.resourceOne.findIndex((rc) => rc.id === id);
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = async (id) => {
    const valueIndex = getValueIndex(values, id);

    const fieldsToValidate = [
      `resourceOne.${valueIndex}.resourceId`,
      `resourceOne.${valueIndex}.leader`,
      `resourceOne.${valueIndex}.educationDate`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
      if (errors?.resourceOne?.[valueIndex]?.[field.split('.').pop()]) {
        return;
      }
    }

    setRowModesModel((prevModel) => ({
      ...prevModel,
      [id]: { mode: GridRowModes.View },
    }));
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const columns = (setFieldValue, validateField, errors, remove) => {
    return dataGridPremiumColumns([
      {
        field: 'resourceId',
        headerName: 'Név',
        type: 'string',
        editable: true,
        renderCell: (data) => {
          const findedResourceOne = autoCompleteOptions?.resourceOne.find(
            (item) => item.value === data.row?.resourceId
          );
          return findedResourceOne?.label;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedResourceOne = autoCompleteOptions.resourceOne.find((item) => item.value === data.row.resourceId);

          return (
            <Field
              name={`resourceOne.${valueIndex}.resourceId`}
              component={AutoCompleteSelectUI}
              helperText={<FormErrorUI message={errors?.resourceOne?.[valueIndex]?.resourceId} />}
              selectedValue={values.resourceOne?.[valueIndex]?.resourceId}
              selectedLabelValue={findedResourceOne?.label}
              onChange={(_e, newVal) => {
                setFieldValue(`resourceOne.${valueIndex}.resourceId`, newVal?.value ?? null);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newVal.value ?? null,
                });
              }}
              dataset={autoCompleteOptions.resourceOne}
              onBlur={async () => await validateField(`resourceOne.${valueIndex}.resourceId`)}
            />
          );
        },
      },
      {
        field: 'leader',
        type: 'string',
        headerName: 'Vezető',
        editable: true,
        renderCell: (data) => <CheckboxUI checked={data.row?.leader === true} disabled />,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              name={`resourceOne.${valueIndex}.leader`}
              component={CheckboxUI}
              helperText={<FormErrorUI message={errors?.resourceOne?.[valueIndex]?.leader} />}
              checked={data.row?.leader === true}
              variant="standard"
              onChange={(e) => {
                setFieldValue(`resourceOne.${valueIndex}.leader`, e.target.checked ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.checked,
                });
              }}
              onBlur={async () => await validateField(`resourceOne.${valueIndex}.leader`)}
            />
          );
        },
      },
      ...(params.PROJECT_RES_ONE_EDUCATION_DATE_FIELD === 'Y'
        ? [
            {
              field: 'educationDate',
              headerName: 'Okt. dátuma',
              sortable: false,
              type: 'date',
              editable: true,
              valueGetter: (_data, row) => {
                return row.educationDate ? new Date(row.educationDate) : '';
              },
              renderEditCell: (data) => {
                const valueIndex = getValueIndex(values, data.id);
                return (
                  <Field
                    name={`resourceOne.${valueIndex}.educationDate`}
                    component={DatePickerUI}
                    helperText={<FormErrorUI message={errors?.resourceOne?.[valueIndex]?.educationDate} />}
                    variant="standard"
                    value={dayjs(data.row.educationDate)}
                    onChange={(newDate) => {
                      const finalDate = newDate ? newDate.startOf('day').add(12, 'hour').toDate() : null;

                      setFieldValue(`resourceOne.${valueIndex}.educationDate`, finalDate);

                      data.api.setEditCellValue({
                        id: data.id,
                        field: data.field,
                        value: finalDate,
                      });
                    }}
                    onBlur={async () => await validateField(`resourceOne.${valueIndex}.educationDate`)}
                  />
                );
              },
            },
          ]
        : []),
      ...(params.PROJECT_SHOW_JOB_IN_RESOURCE === 'Y'
        ? [
            {
              field: 'simpleTasksSummary',
              headerName: 'Munka',
              type: 'string',
              sortable: false,
              editable: false,
              renderCell: (params) => {
                const taskList = params.row.simpleTask || [];

                if (!taskList.length) return null;

                const formattedTasks = taskList.map((task) => {
                  const start = task.startDate ? dayjs(task.startDate).format('YYYY.MM.DD.') : '';
                  const end = task.endDate ? dayjs(task.endDate).format('YYYY.MM.DD.') : '';
                  const dateRange = start && end ? `${start} - ${end}` : start || end || '';
                  return task.simpleTaskName ? `${dateRange}` : `Feladat ${task.simpleTaskId} (${dateRange})`;
                });

                return formattedTasks.join(', ');
              },
            },
          ]
        : []),
      ...(params.PROJECT_RES_ONE_REGISTERED_FIELD === 'Y'
        ? [
            {
              field: 'registered',
              headerName: 'Bejelentve',
              type: 'string',
              editable: true,
              renderCell: (data) => <CheckboxUI checked={data.row?.registered === true} disabled />,
              renderEditCell: (data) => {
                const valueIndex = getValueIndex(values, data.id);
                return (
                  <Field
                    name={`resourceOne.${valueIndex}.registered`}
                    component={CheckboxUI}
                    helperText={<FormErrorUI message={errors?.resourceOne?.[valueIndex]?.registered} />}
                    checked={data.row?.registered === true}
                    variant="standard"
                    onChange={(e) => {
                      setFieldValue(`resourceOne.${valueIndex}.registered`, e.target.checked ?? '');
                      data.api.setEditCellValue({
                        id: data.id,
                        field: data.field,
                        value: e.target.checked,
                      });
                    }}
                    onBlur={async () => await validateField(`resourceOne.${valueIndex}.registered`)}
                  />
                );
              },
            },
          ]
        : []),
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id }) => {
          const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
          if (isInEditMode) {
            return [
              <GridActionsCellItem
                icon={<CheckIcon />}
                label="Save"
                onClick={() => handleSaveClick(id)}
                color="inherit"
              />,
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                onClick={() => {
                  const valueIndex = getValueIndex(values, id);
                  return remove(valueIndex);
                }}
                color="inherit"
              />,
            ];
          }

          return [
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit"
              onClick={handleEditClick(id)}
              color="inherit"
              disabled={disabled}
            />,
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const valueIndex = getValueIndex(values, id);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);
  };

  const getRowClassName = (params) => {
    let classNames = '';

    if (!params.row.registered) classNames += 'dataGrid-line-not-registered';

    return classNames.trim();
  };

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="resourceOne">
            {({ remove, push }) => (
              <DatagridPremiumUi
                datagridIdentifier={DataGridIdentifiers.ResourceOneTable}
                height={400}
                columns={columns(setFieldValue, validateField, errors, remove)}
                rows={values?.resourceOne ?? []}
                rowModesModel={rowModesModel}
                onRowModesModelChange={handleRowModesModelChange}
                onRowEditStop={handleRowEditStop}
                slots={{
                  toolbar: EditToolbar,
                }}
                headerFilters={false}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    defaultResourceOne,
                    setRowModesModel,
                    params,
                    dynProps,
                  },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                getRowClassName={getRowClassName}
                getRowHeight={() => 'auto'}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default ResourceOneTable;
